import { useEffect, useState } from "react";
import api from "../../lib/api";

/**
 * ResultColumnsPicker — PHASE 8.36
 *
 * Per-wave picker: which demographic dimensions should be exposed alongside
 * this wave's results (public site, panelist dashboard, subscriber +
 * enterprise hubs). The user said: "for a wave, i should be able to select
 * what things to show in results, even the profile dnas thing".
 *
 * Implementation:
 *   - Pulls the full DNA field list from /admin/profile-dna/public (so the
 *     picker reflects the live DNA schema — new fields appear automatically).
 *   - Hard-coded "Core demographics" pseudo-rows for the 6 identity fields
 *     (region, gender, age_band, sec, city, country_of_residence). These
 *     never go through DNA but are first-class result dimensions.
 *   - Empty selection = "use defaults" (region, gender, age_band, sec).
 *     Defaults are computed downstream — admin sees an explanatory note.
 *
 * NOT a collection toggle. Patrons still answer every required field; this
 * only controls VISIBILITY downstream in cross-tabs and result panels.
 */
const CORE_OPTIONS = [
  { key: "region",               label: "Region",               group: "Core demographics" },
  { key: "gender",               label: "Gender",               group: "Core demographics" },
  { key: "age_band",             label: "Age band",             group: "Core demographics" },
  { key: "sec",                  label: "Socio-economic class", group: "Core demographics" },
  { key: "city",                 label: "City",                 group: "Core demographics" },
  { key: "country_of_residence", label: "Country of residence", group: "Core demographics" },
];

const DEFAULT_HINT = "region · gender · age_band · sec";

export default function ResultColumnsPicker({ value, onChange }) {
  const [dnaRows, setDnaRows] = useState([]);
  const selected = new Set(value || []);

  useEffect(() => {
    api.get("/admin/profile-dna/public")
      .then((r) => setDnaRows((r.data || []).filter((f) => !f.is_core && !f.hidden)))
      .catch(() => setDnaRows([]));
  }, []);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  };

  const selectAllCore = () => {
    const next = new Set(selected);
    CORE_OPTIONS.forEach((c) => next.add(c.key));
    onChange(Array.from(next));
  };

  const reset = () => onChange([]);

  // Group DNA rows by section for a clean UI
  const dnaGroups = (() => {
    const m = new Map();
    for (const f of dnaRows) {
      const sec = f.section || "Custom";
      if (!m.has(sec)) m.set(sec, []);
      m.get(sec).push({ key: f.key, label: f.label, group: sec });
    }
    return Array.from(m.entries());
  })();

  return (
    <div className="bg-white border-2 border-black p-5" data-testid="wb-result-columns">
      <Header selectedCount={selected.size} onSelectAllCore={selectAllCore} onReset={reset} />
      <p className="text-xs text-neutral-500 mb-3">
        Decide which demographic dimensions show alongside this wave&apos;s results — in public charts, panelist dashboards, subscriber views, and enterprise reports.
        <br />
        <span className="text-neutral-700">
          Empty selection = use system default (<span className="font-mono">{DEFAULT_HINT}</span>).
          Patrons still answer every required field — this only controls what&apos;s surfaced in cross-tabs.
        </span>
      </p>

      <Group title="Core demographics" items={CORE_OPTIONS} selected={selected} toggle={toggle} testidPrefix="result-col-core" />

      {dnaGroups.length === 0 && (
        <div className="text-xs text-neutral-400 italic mt-3">No custom DNA fields configured yet — manage them at /admin/profile-dna.</div>
      )}
      {dnaGroups.map(([sec, items]) => (
        <Group key={sec} title={sec} items={items} selected={selected} toggle={toggle} testidPrefix="result-col-dna" />
      ))}
    </div>
  );
}

function Header({ selectedCount, onSelectAllCore, onReset }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
      <label className="overline mb-1 flex items-center gap-1">Result columns — what to show alongside this wave&apos;s results</label>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-neutral-600" data-testid="result-col-selected-count">{selectedCount} selected</span>
        <button type="button" onClick={onSelectAllCore} className="underline hover:text-black" data-testid="result-col-select-core">+ all core demographics</button>
        {selectedCount > 0 && <button type="button" onClick={onReset} className="underline text-neutral-600 hover:text-black" data-testid="result-col-reset">reset</button>}
      </div>
    </div>
  );
}

function Group({ title, items, selected, toggle, testidPrefix }) {
  if (!items.length) return null;
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-3 mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {items.map((it) => {
          const isOn = selected.has(it.key);
          return (
            <label
              key={it.key}
              className={`flex items-center gap-2 text-xs p-2 border-2 cursor-pointer transition-colors ${isOn ? "border-black bg-mango/10" : "border-neutral-200 hover:border-black"}`}
              data-testid={`${testidPrefix}-${it.key}`}
            >
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => toggle(it.key)}
                className="w-4 h-4"
              />
              <div className="min-w-0">
                <div className="font-bold truncate">{it.label}</div>
                <div className="font-mono text-[10px] text-neutral-500 truncate">{it.key}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
